/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {
    Box,
    Flex,
    Button,
    Stack,
    Text,
    Divider,
    useMultiStyleConfig,
    HStack,
    VStack,
    Portal,
    Link,
    useBreakpointValue,
} from '@chakra-ui/react'
import {PromoCode, usePromoCode} from './partials/promo-code'
import ZipCodeWrapper, {useZipCode} from './partials/zip-code-preview'
import {useLocation} from 'react-router-dom'
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import {usePromotions, useProducts} from '@salesforce/commerce-sdk-react'
import {remCalc} from '../../theme/_utils'
import ItemPriceCustom from '../item-variant/item-price-custom'
import OrderSummaryDivider from './partials/divider'
import {DeleteIcon, InfoIcon} from '../icons'
import KlarnaWidget from '../klarna-widget'
import PaypalWidget from '../paypal-widget'
import VatLabel from '../vat-label'
import useUtils from '../../custom-hooks/use-utils-helper'
import InfoPopover from '../popovers/info-popover'
import WeeeFee from '../weee-fee'
import {
    useBooleanCustomPreference,
    useCustomPreferenceWithFallback,
    useCustomPreferenceWithLocale,
} from '../../hooks/use-custom-preference'
import OrderTotal from './partials/orderTotal'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import Checkbox from '../checkbox'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import RetailLink from '@salesforce/retail-react-app/app/components/link'
import SubscriptionItem from '../subscription-item'
import PaymentMethodLabel from '../payment-method-label'
import {getOrderAvailabilityStatus, getFutureStockDate} from '../../utils/order-availability-utils'
import CartCta from '../../pages/cart/partials/cart-cta'
import useIntersectionObserver from '../../custom-hooks/use-intersection-observer'

const CartItems = ({basket, productsByItemId}) => {
    const totalItems = basket?.productItems?.reduce((acc, item) => acc + item.quantity, 0) || 0
    const location = useLocation()
    const {isCart} = useUtils().currentPage(location.pathname)
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('OrderSummary', {basketLength: basket?.productItems?.length})
    const allSubscriptionsDetails = basket?.c_allSubscriptionsDetails
        ? JSON.parse(basket.c_allSubscriptionsDetails)
        : {}

    return (
        <Box sx={styles.cartItems.container}>
            {isCart && (
                <Text {...styles.cartItems.itemsText}>
                    <FormattedMessage
                        defaultMessage="Items ({totalItems})"
                        id="order_summary.cart_items.link.items_in_cart"
                        values={{totalItems}}
                    />
                </Text>
            )}
            {Object.entries(allSubscriptionsDetails).map(([bundleId, details]) => {
                return (
                    <Box key={bundleId} {...styles.cartItems.itemsContainer}>
                        <SubscriptionItem details={details} variant={'subscriptionsOrderSummary'} />
                        <Divider mt="medium.base" />
                    </Box>
                )
            })}
            {/* we need to filter out warranty items */}
            {basket.productItems
                ?.filter((product) => !product.c_isWarranty)
                .map((product, idx) => {
                    if (product?.c_isSubscription === 'true') return
                    const variant = {
                        ...product,
                        ...(productsByItemId && productsByItemId[product.itemId]),
                        price: product?.price,
                        quantity: product?.quantity,
                    }
                    const orderStatus = getOrderAvailabilityStatus(variant)
                    const futureStockDate = getFutureStockDate(
                        variant,
                        formatMessage.locale || 'it-IT',
                    )
                    const associatedWarranty = basket?.productItems?.find(
                        (item) =>
                            item.c_isWarranty === true &&
                            item.c_UUIDWarranty === product.c_UUIDWarranty,
                    )
                    const showFutureStockLabel =
                        (orderStatus.isPreorder || orderStatus.isBackorder) && futureStockDate
                    return (
                        <Box key={idx} {...styles.cartItems.itemsContainer}>
                            <ItemVariantProvider index={idx} variant={variant}>
                                <Flex {...styles.cartItems.innerItemContainer}>
                                    <CartItemVariantImage
                                        imageProps={{width: remCalc(109)}}
                                        boxWidth={remCalc(109)}
                                        minWidth={remCalc(109)}
                                    />
                                    <Stack {...styles.cartItems.stack}>
                                        <CartItemVariantName {...styles.cartItems.itemVariant} />
                                        {showFutureStockLabel && (
                                            <Text
                                                textStyle="body-xsmall"
                                                fontWeight="normal"
                                                color="text.default"
                                            >
                                                <FormattedMessage
                                                    id="order_summary.message.available_from"
                                                    defaultMessage="This item will be available from {date}"
                                                    values={{
                                                        date: (
                                                            <Text as="span" fontWeight="bold">
                                                                {futureStockDate}
                                                            </Text>
                                                        ),
                                                    }}
                                                />
                                            </Text>
                                        )}
                                        <Flex {...styles.cartItems.stackContent}>
                                            <ItemPriceCustom
                                                currency={basket?.currency}
                                                styles={{}}
                                                direction="row"
                                                showOmnibusLabel={false}
                                                styleVariant="checkout"
                                            />
                                            <CartItemVariantAttributes
                                                includeQuantity
                                                includeVariables={false}
                                                associatedWarranty={associatedWarranty}
                                                {...styles.cartItems.itemVariantAttributes}
                                            />
                                        </Flex>
                                    </Stack>
                                </Flex>
                            </ItemVariantProvider>
                            <Divider mt="medium.base" />
                        </Box>
                    )
                })}
        </Box>
    )
}

CartItems.propTypes = {basket: PropTypes.object, productsByItemId: PropTypes.object}

const OrderSummary = ({
    basket,
    // showPromoCodeForm = false,
    showCartItems = false,
    isEstimate = false,
    showPromoCodeForm = true,
    variant,
    productsByItemId,
    handleUnavailableProducts,
    showSubscriptionsCheck = false,
    step,
    validateWarrantyBeforeAction,
    refetchBasket,
}) => {
    const {removePromoCode, ...promoCodeProps} = usePromoCode(refetchBasket)
    const {...zipCodeProps} = useZipCode()
    const style = useMultiStyleConfig('OrderSummary', {variant})
    const location = useLocation()
    const {isCart, isCheckout} = useUtils().currentPage(location.pathname)
    const isVATFlag = useBooleanCustomPreference('showVATFlag')
    const avalaraStorefrontPreference = useBooleanCustomPreference('avalaraStorefrontPreference')
    const isAvalaraEnabled =
        avalaraStorefrontPreference !== null ? avalaraStorefrontPreference : false
    const navigate = useNavigation()
    const {formatMessage} = useIntl()
    const [salesTermsAccepted, setSalesTermsAccepted] = useState(false)
    const [salesTermsError, setSalesTermsError] = useState(false)
    const termsCheckboxRef = useRef(null)
    const orderTotalRef = useRef(null)
    const checkoutButtonRef = useRef(null)
    const isOrderTotalVisible = useIntersectionObserver(orderTotalRef, {
        useOnce: false,
        threshold: 0,
        rootMargin: '0px',
    })
    const isCheckoutButtonVisible = useIntersectionObserver(checkoutButtonRef, {
        useOnce: false,
        threshold: 0,
        rootMargin: '0px',
    })
    const isMobileViewport = useBreakpointValue({base: true, lg: false}) ?? true
    const isStickyCheckout =
        isCart && isMobileViewport && !isOrderTotalVisible && !isCheckoutButtonVisible

    const subscriptionRedirectPDP = useCustomPreferenceWithFallback('subscriptionRedirectPDP')

    const warrantyTermsAndConditionsLinkLocale = useCustomPreferenceWithLocale(
        'warrantyTermsAndConditionsLink',
    )
    const warrantyTermsAndConditionsLinkFallback = useCustomPreferenceWithFallback(
        'warrantyTermsAndConditionsLink',
    )
    const warrantyTermsAndConditionsLink =
        warrantyTermsAndConditionsLinkLocale || warrantyTermsAndConditionsLinkFallback

    const bundles = basket?.c_additionalData?.allBundlesDetails || {}

    const {locale} = useMultiSite()

    const {refetch: refetchProducts} = useProducts(
        {
            parameters: {
                ids: basket?.productItems?.map((i) => i.productId).join(','),
                allImages: false,
            },
            headers: {
                'x-debug': 'order-summary-index',
            },
        },
        {
            enabled: false,
        },
    )

    const productPriceAdjustments =
        basket?.productItems?.reduce((acc, product) => {
            if (product.priceAdjustments) {
                acc.push(
                    ...product.priceAdjustments.filter(
                        (adj) => !adj.promotionId?.startsWith('bundle'),
                    ),
                )
            }
            return acc
        }, []) ?? []

    const weeeFeeItems = productPriceAdjustments.filter((item) => item.promotionId === 'weeeFee')

    // Sum the prices of those items
    const weeeFeeTotal = weeeFeeItems.reduce((sum, item) => sum + item.price, 0)

    const allPriceAdjustments = [
        ...(productPriceAdjustments ?? []),
        ...(basket?.orderPriceAdjustments ?? []),
    ]
    const allPromotionIds = allPriceAdjustments.map((adj) => adj?.promotionId)

    const {data: promos} = usePromotions(
        {parameters: {ids: allPromotionIds.join(',')}},
        {enabled: allPromotionIds.length > 0},
    )

    const warrantyItems = basket?.productItems?.filter((item) => item.c_isWarranty) || []
    const warrantyTotal =
        warrantyItems?.length > 0
            ? warrantyItems?.reduce((total, item) => total + item.price, 0)
            : null

    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }
    // const shippingItem = basket?.shippingItems?.[0]
    // const hasShippingPromos = shippingItem?.priceAdjustments?.length > 0

    const calcSubtotal = (basket) => {
        let subtotal = 0
        // to calculate the subtotal, the prices of all items are added together except for free gifts, which are identified as type bonus
        if (basket?.productItems) {
            for (let item of basket.productItems) {
                const hasBonusAdjustment = item.priceAdjustments?.some(
                    (adjustment) => adjustment.appliedDiscount?.type === 'bonus',
                )

                if (!hasBonusAdjustment) {
                    subtotal += item.price
                }
            }
        }

        return subtotal || 0
    }

    const handleGoToCheckout = async () => {
        if (showSubscriptionsCheck && !salesTermsAccepted) {
            setSalesTermsError(true)
            termsCheckboxRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
            return
        }
        if (validateWarrantyBeforeAction && !validateWarrantyBeforeAction()) {
            return
        }
        const ids = basket?.productItems?.map((i) => i.productId)
        const result = await refetchProducts()

        const resProductIds = []
        const unOrderableIds = []
        result?.data?.data?.forEach((product) => {
            resProductIds.push(product.id)
            const item = basket.productItems.find((i) => i.productId === product.id)
            const isWishlist = !!item?.type

            // Check if product is preorder or backorder
            const orderStatus = getOrderAvailabilityStatus(product)
            const isPreorder = orderStatus?.isPreorder
            const isBackorder = orderStatus?.isBackorder

            if (!isWishlist) {
                if (isPreorder || isBackorder) {
                    // For preorder/backorder, skip stock validation
                    // The UnavailableProductConfirmationModal will handle custom limit checks
                } else {
                    // Standard stock validation for non-preorder/backorder products
                    if (
                        !product.inventory?.orderable ||
                        (product.inventory?.orderable &&
                            item.quantity > product.inventory.stockLevel)
                    ) {
                        unOrderableIds.push(product.id)
                    }
                }
            }
        })

        const unavailable = ids.filter(
            (id) => !resProductIds.includes(id) || unOrderableIds.includes(id),
        )

        if (unavailable.length > 0) {
            handleUnavailableProducts(unavailable)
        } else {
            navigate('/checkout')
        }
    }

    return (
        <Box {...style?.orderSummaryBox}>
            {isCart && (
                <>
                    {showSubscriptionsCheck && (
                        <Box {...style?.subscriptionsContainer}>
                            <Flex ref={termsCheckboxRef}>
                                <Box>
                                    <Checkbox
                                        variant={'secondary'}
                                        onChange={(e) => {
                                            setSalesTermsAccepted(e.target.checked)
                                            setSalesTermsError(false)
                                        }}
                                        isError={salesTermsError}
                                    />
                                </Box>
                                <Flex {...style?.subscriptionsTextsContainer}>
                                    <Text {...style?.subscriptionsText}>
                                        <FormattedMessage
                                            id="order_summary.subscriptions.accept_text"
                                            defaultMessage="I accept the"
                                        />
                                    </Text>
                                    <RetailLink to={`${locale?.saleTermsAndConditions}`}>
                                        <Text {...style?.subscriptionsLink}>
                                            <FormattedMessage
                                                id="order_summary.subscriptions.accept_link"
                                                defaultMessage="terms and conditions of sale"
                                            />
                                        </Text>
                                    </RetailLink>
                                </Flex>
                            </Flex>
                            {salesTermsError && (
                                <Text {...style?.errorText}>
                                    <FormattedMessage
                                        id="order_summary.subscriptions.terms_error"
                                        defaultMessage="*Obbligatory field"
                                    />
                                </Text>
                            )}
                        </Box>
                    )}
                </>
            )}

            <Stack
                {...style?.summaryContainer}
                style={
                    variant !== 'checkout'
                        ? {
                              boxShadow:
                                  '0 0 0 1px var(--chakra-colors-border-neutral-highlight) inset, 0 1px 2px 0 var(--chakra-shadows-neutral-xs), 0 0 0 1px var(--chakra-shadows-neutral-xs)',
                          }
                        : {}
                }
                data-testid="sf-order-summary"
            >
                <Box {...style?.innerSummaryContainer}>
                    {showPromoCodeForm && (
                        <Box {...style?.promo?.promoContainer} id="mcp-cart-container">
                            <PromoCode {...promoCodeProps} />

                            {locale?.alias === 'en-gb' && (
                                <Flex {...style?.promo?.referralLinkContainer}>
                                    <Text {...style?.promo?.referralLinkText}>
                                        <FormattedMessage
                                            defaultMessage="Been referred by a friend?"
                                            id="promo_code_fields.referral_link_text"
                                        />
                                    </Text>

                                    <Button
                                        variant="link-button"
                                        size="base"
                                        onClick={() =>
                                            window.open(
                                                'https://mention-me.com/me/referee/referee_search_integration/mm0dcc4f72/9ea2fa82715022d3a1a5ec2243323ec7f5d4e5d0/p/checkout?implementation=link&locale=en_GB',
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }
                                    >
                                        <FormattedMessage
                                            defaultMessage="Learn More"
                                            id="promo_code_fields.referral_link_button"
                                        />
                                    </Button>
                                </Flex>
                            )}
                        </Box>
                    )}

                    <Flex {...style?.summaryDetailsContainer}>
                        <Box {...style?.summaryHeader?.itemContainer}>
                            <Text {...style?.summaryHeader?.headerText} id="order-summary-heading">
                                <FormattedMessage
                                    defaultMessage="Order Review"
                                    id="order_summary.heading.order_review"
                                />
                            </Text>
                        </Box>
                        {showCartItems && isCart && (
                            <>
                                <OrderSummaryDivider />
                                <CartItems basket={basket} productsByItemId={productsByItemId} />
                                <OrderSummaryDivider />
                            </>
                        )}

                        <Box {...style?.subtotalSection?.subtotalContainer}>
                            <HStack {...style?.subtotalSection?.subtotalStackContainer}>
                                <Text {...style?.subtotalSection?.subtotalText}>
                                    <FormattedMessage
                                        defaultMessage="Subtotal"
                                        id="order_summary.label.subtotal"
                                    />
                                </Text>
                                <Text {...style?.subtotalSection?.subtotalPrice}>
                                    <FormattedNumber
                                        style="currency"
                                        currency={basket?.currency}
                                        value={calcSubtotal(basket)}
                                    />
                                </Text>
                            </HStack>
                        </Box>
                        {basket?.couponItems?.length > 0 &&
                            basket?.couponItems?.[0]?.valid &&
                            basket?.couponItems?.[0].statusCode === 'no_applicable_promotion' && (
                                <Stack {...style?.promo?.promoAppliedOuterContainer}>
                                    <Text {...style?.promo?.promoAppliedTitle}>
                                        <FormattedMessage
                                            id="use_promo_code_fields.label.promo_code"
                                            defaultMessage="Promo Code"
                                        />
                                    </Text>

                                    <Flex {...style?.promo?.promoAppliedContainer}>
                                        <Text {...style?.promo?.appliedPromoText}>
                                            {basket.couponItems[0]?.code}
                                        </Text>

                                        {isCart && !basket.orderNo && (
                                            <DeleteIcon
                                                onClick={() =>
                                                    removePromoCode(
                                                        basket.couponItems[0]?.couponItemId,
                                                    )
                                                }
                                                aria-label={formatMessage({
                                                    id: 'order_summary.promo_code.remove.aria_label',
                                                    defaultMessage: 'Remove promo code',
                                                })}
                                                role="button"
                                                {...style?.promo?.removePromoIcon}
                                            />
                                        )}
                                    </Flex>
                                </Stack>
                            )}
                        <VStack {...style?.subtotalSection?.shippingContainer}>
                            <HStack {...style?.subtotalSection?.subtotalStackContainer}>
                                <HStack {...style?.subtotalSection?.shippingTextContainer}>
                                    <Text {...style?.subtotalSection?.subtotalText}>
                                        <FormattedMessage
                                            defaultMessage="Delivery"
                                            id="order_summary.label.shipping"
                                        />
                                    </Text>
                                    <Box {...style?.subtotalSection?.infoIconContainer}>
                                        <InfoPopover
                                            variant="top-right"
                                            trigger={
                                                <InfoIcon
                                                    {...style?.subtotalSection?.infoIcon}
                                                    aria-hidden="true"
                                                    tabIndex={0}
                                                    focusable="true"
                                                />
                                            }
                                            openTooltipOn="hover"
                                            title={formatMessage({
                                                id: 'order_summary.delivery.info.title',
                                                defaultMessage: 'Delivery Information',
                                            })}
                                            description={formatMessage({
                                                id: 'order_summary.delivery.info.description',
                                                defaultMessage:
                                                    'Delivery details and estimated arrival time',
                                            })}
                                        />
                                    </Box>
                                </HStack>

                                {basket?.shippingTotal > 0 ? (
                                    <Text {...style?.subtotalSection?.subtotalPrice}>
                                        <FormattedNumber
                                            value={basket.shippingTotal}
                                            style="currency"
                                            currency={basket?.currency}
                                        />
                                    </Text>
                                ) : (
                                    <Text {...style?.subtotalSection?.freeShippingText}>
                                        <FormattedMessage
                                            defaultMessage="Free shipping"
                                            id="order_summary.label.free_shipping"
                                        />
                                    </Text>
                                )}
                            </HStack>
                            <PaymentMethodLabel basket={basket} style={style} />
                        </VStack>
                        {warrantyTotal > 0 && (
                            <HStack {...style?.subtotalSection?.subtotalStackContainer}>
                                <Text {...style?.subtotalSection?.subtotalText}>
                                    <FormattedMessage
                                        defaultMessage="Product Services"
                                        id="order_summary.label.product_services"
                                    />
                                </Text>
                                <Text {...style?.subtotalSection?.subtotalPrice}>
                                    <FormattedNumber
                                        style="currency"
                                        currency={basket?.currency}
                                        value={warrantyTotal}
                                    />
                                </Text>
                            </HStack>
                        )}

                        {allPriceAdjustments.map(
                            (adjustment) =>
                                adjustment.promotionId !== 'weeeFee' &&
                                !adjustment.promotionId?.startsWith('Klarna0tax_') && (
                                    <Box
                                        key={adjustment.priceAdjustmentId}
                                        {...style.subtotalSection.subtotalPriceContainer}
                                    >
                                        <Flex
                                            {...style?.subtotalSection?.subtotalPromotionContainer}
                                        >
                                            <Text {...style?.subtotalSection?.subtotalText}>
                                                <Text {...style?.subtotalSection?.subtotalText}>
                                                    {
                                                        promos?.data?.find(
                                                            (promo) =>
                                                                promo.id === adjustment.promotionId,
                                                        )?.calloutMsg
                                                    }
                                                </Text>
                                            </Text>

                                            {adjustment.couponCode && (
                                                <Flex {...style?.promo?.promoAppliedContainer}>
                                                    <Text {...style?.promo?.appliedPromoText}>
                                                        {adjustment.couponCode}
                                                    </Text>

                                                    {isCart && !basket.orderNo && (
                                                        <DeleteIcon
                                                            onClick={() =>
                                                                removePromoCode(
                                                                    basket.couponItems[0]
                                                                        ?.couponItemId,
                                                                )
                                                            }
                                                            aria-label={formatMessage({
                                                                id: 'order_summary.promo_code.remove.aria_label',
                                                                defaultMessage: 'Remove promo code',
                                                            })}
                                                            role="button"
                                                            {...style?.promo?.removePromoIcon}
                                                        />
                                                    )}
                                                </Flex>
                                            )}
                                        </Flex>

                                        <Text {...style.subtotalSection.subtotalPrice}>
                                            <FormattedNumber
                                                style="currency"
                                                currency={basket?.currency}
                                                value={adjustment.price}
                                            />
                                        </Text>
                                    </Box>
                                ),
                        )}

                        {bundles &&
                            Object.entries(bundles).map(([bundleId, details]) => {
                                const discount =
                                    details?.totalPrice - details?.totalDiscountedPrice || 0
                                if (discount > 0) {
                                    return (
                                        <Box
                                            key={bundleId}
                                            {...style.subtotalSection.subtotalPriceContainer}
                                        >
                                            <Text {...style?.subtotalSection?.subtotalText}>
                                                {details?.bundleName}
                                            </Text>
                                            <Text {...style.subtotalSection.subtotalPrice}>
                                                <FormattedNumber
                                                    style="currency"
                                                    currency={basket?.currency}
                                                    value={discount * -1}
                                                />
                                            </Text>
                                        </Box>
                                    )
                                }
                            })}

                        {weeeFeeTotal > 0 && (
                            <WeeeFee price={weeeFeeTotal} variant="orderSummary" />
                        )}

                        {isAvalaraEnabled && (
                            <ZipCodeWrapper
                                flex-direction="column"
                                {...zipCodeProps}
                                basket={basket}
                                {...style.zipCode.zipCodeContainer}
                                step={step}
                                key={`${basket?.productSubTotal} ${
                                    !step || step === 1 || step === 2
                                        ? 'showInputStep'
                                        : 'notShowInputStep'
                                }`}
                            />
                        )}
                    </Flex>
                </Box>

                <OrderSummaryDivider />

                {(isCart || isCheckout) && (
                    <Box ref={orderTotalRef} {...style?.checkoutSection?.checkoutContainer}>
                        {isEstimate ? (
                            <Text {...style?.checkoutSection?.totalText}>
                                <FormattedMessage
                                    defaultMessage="TOTAL"
                                    id="order_summary.label.estimated_total"
                                />
                            </Text>
                        ) : (
                            <Text {...style?.checkoutSection?.totalText}>
                                <FormattedMessage
                                    defaultMessage="Order Total"
                                    id="order_summary.label.order_total"
                                />
                            </Text>
                        )}
                        <Flex {...style?.checkoutSection?.totalPriceContainer}>
                            <Text {...style?.checkoutSection?.totalPrice}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={basket?.orderTotal || basket?.productTotal}
                                />
                            </Text>

                            {isVATFlag && <VatLabel amount={basket?.taxTotal} hideVatRate />}

                            {isCart && (
                                <Box w="100%">
                                    <KlarnaWidget
                                        price={{currentPrice: basket?.orderTotal}}
                                        variant={'cart'}
                                    />
                                    <PaypalWidget
                                        price={basket?.orderTotal}
                                        placement="cart"
                                        variant="cart"
                                    />
                                </Box>
                            )}
                        </Flex>
                    </Box>
                )}

                {/* This is to add space beneath the footer to ensure it is not covered by the fixed to the bottom goToCheckout banner when in mobile or tablet view.
                    This is achieved using a Portal with position: relative and a negative z-index.*/}
                {isStickyCheckout && (
                    <Portal>
                        <Box
                            {...style?.checkoutSection?.goToCheckoutButtonContainer}
                            position={'relative'}
                            zIndex={-1}
                            visibility="hidden"
                            aria-hidden="true"
                            display="block"
                        >
                            <OrderTotal
                                isEstimate={isEstimate}
                                isVATFlag={isVATFlag}
                                basket={basket}
                            />

                            <Button
                                as="button"
                                tabIndex={-1}
                                data-cs-label="startCheckout"
                                onClick={handleGoToCheckout}
                                {...style?.checkoutSection?.goToCheckout}
                                variant="primary-not-rounded"
                                size="large"
                                aria-label={formatMessage({
                                    id: 'order_summary.checkout_button.aria_label',
                                    defaultMessage: 'Proceed to checkout',
                                })}
                                sx={{
                                    touchAction: 'manipulation',
                                    '& > *': {
                                        pointerEvents: 'none',
                                    },
                                }}
                            >
                                <Text>
                                    <FormattedMessage
                                        id="order_summary.checkout_button"
                                        defaultMessage="Go to Checkout"
                                    />
                                </Text>
                            </Button>
                        </Box>
                    </Portal>
                )}

                {isCart && (
                    <Box
                        {...style?.checkoutSection?.goToCheckoutButtonContainer}
                        position="static"
                        left="unset"
                        bottom="unset"
                        background="unset"
                        px="0"
                        zIndex="auto"
                    >
                        <Button
                            ref={checkoutButtonRef}
                            as="button"
                            data-cs-label="startCheckout"
                            {...style?.checkoutSection?.goToCheckout}
                            onClick={handleGoToCheckout}
                            variant="primary"
                            height={remCalc(56)}
                            aria-label={formatMessage({
                                id: 'order_summary.checkout_button.aria_label',
                                defaultMessage: 'Proceed to checkout',
                            })}
                            sx={{
                                touchAction: 'manipulation',
                                '& > *': {pointerEvents: 'none'},
                            }}
                        >
                            <Text>
                                <FormattedMessage
                                    id="order_summary.checkout_button"
                                    defaultMessage="Go to Checkout"
                                />
                            </Text>
                        </Button>
                    </Box>
                )}

                {/* This is the sticky goToCheckout button banner that is fixed at the bottom of the screen*/}
                {isStickyCheckout && (
                    <Box
                        {...style?.checkoutSection?.goToCheckoutButtonContainer}
                        {...style?.checkoutSection?.goToCheckoutButtonContainerSticky}
                        position="fixed"
                        left="0"
                        bottom="0"
                        background="background.light"
                        px="small.base"
                        zIndex="3"
                    >
                        <OrderTotal isEstimate={isEstimate} isVATFlag={isVATFlag} basket={basket} />
                        <Button
                            as="button"
                            data-cs-label="startCheckout"
                            {...style?.checkoutSection?.goToCheckout}
                            onClick={async () => {
                                handleGoToCheckout()
                            }}
                            variant="primary-not-rounded"
                            size="large"
                            height={remCalc(36)}
                            aria-label={formatMessage({
                                id: 'order_summary.checkout_button.aria_label',
                                defaultMessage: 'Proceed to checkout',
                            })}
                            sx={{
                                touchAction: 'manipulation',
                                '& > *': {
                                    pointerEvents: 'none',
                                },
                            }}
                        >
                            <Text>
                                <FormattedMessage
                                    id="order_summary.checkout_button"
                                    defaultMessage="Go to Checkout"
                                />
                            </Text>
                        </Button>
                    </Box>
                )}

                {isCart && (
                    <Flex {...style?.checkoutSection?.warrantySubsLinksAndPayementMethodsContainer}>
                        <Text {...style?.checkoutSection?.warrantySubsLinksContainer}>
                            <FormattedMessage
                                id="order_summary.warranty_subs_links"
                                defaultMessage="By proceeding I agree to have read and agree with the terms & conditions for {warrantyLink} and {subscriptionLink}"
                                values={{
                                    warrantyLink: (
                                        <Link
                                            href={warrantyTermsAndConditionsLink}
                                            isExternal
                                            {...style?.checkoutSection?.warrantySubsLinks}
                                        >
                                            <FormattedMessage
                                                id="order_summary.warranty_link"
                                                defaultMessage="Extended Warranty"
                                            />
                                        </Link>
                                    ),
                                    subscriptionLink: (
                                        <RetailLink
                                            to={subscriptionRedirectPDP}
                                            {...style?.checkoutSection?.warrantySubsLinks}
                                        >
                                            <FormattedMessage
                                                id="order_summary.subscription_link"
                                                defaultMessage="Subscriptions"
                                            />
                                        </RetailLink>
                                    ),
                                }}
                            />
                        </Text>

                        <CartCta />
                    </Flex>
                )}

                {showCartItems && isCheckout && (
                    <>
                        <OrderSummaryDivider />
                        <CartItems basket={basket} productsByItemId={productsByItemId} />
                        <OrderSummaryDivider />
                    </>
                )}

                {isCheckout && <CartCta variant="checkout" />}
            </Stack>
        </Box>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    variant: PropTypes.string,
    showPromoCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    fontSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    totalItems: PropTypes.number,
    productsByItemId: PropTypes.object,
    handleUnavailableProducts: PropTypes.func,
    showSubscriptionsCheck: PropTypes.bool,
    step: PropTypes.any,
    validateWarrantyBeforeAction: PropTypes.func,
    refetchBasket: PropTypes.func,
}

export default OrderSummary
